/**
 * W1-11b (app-shell-ui): the files of a drop, folders included (DESIGN.md §4.1 "Dropping several charts or a whole
 * RPE Resources/ folder queues the imports", §5.12; ARCHITECTURE.md §11).
 *
 * A dropped folder reaches `DataTransfer.files` as an unreadable zero-size stub; its contents are reachable only
 * through the drop's entries (`DataTransferItem.webkitGetAsEntry()`), which must be taken while the drop event is
 * being dispatched. So the drop zone calls `takeDropEntries` synchronously in its `drop` listener and expands them
 * afterwards with `expandDropEntries`.
 *
 * Every file that came out of a dropped folder carries its path below the drop as `webkitRelativePath` ("Resources/
 * 12345/chart.json"), exactly like the files of an `<input type="file" webkitdirectory>`, so import code reads both
 * the same way (`relativePathOf`); loose files keep an empty `webkitRelativePath`. Operating-system clutter (hidden
 * dot files, `__MACOSX`, `Thumbs.db`, `desktop.ini`) is skipped. A runaway drop stops at MAX_DROP_FILES files or
 * MAX_DROP_DEPTH folder levels (`truncated`).
 */

/** At most this many files are read from one drop. */
export const MAX_DROP_FILES = 10_000;
/** Folders deeper than this below the drop are not read. */
export const MAX_DROP_DEPTH = 16;
/** Files of a folder read in parallel. */
const FILE_READS_AT_ONCE = 32;

/** The parts of the File and Directory Entries API the drop zone uses (structural, so tests can fake them). */
export interface DropFileEntry {
  readonly isFile: true;
  readonly isDirectory: false;
  readonly name: string;
  file(success: (file: File) => void, error?: (err: unknown) => void): void;
}

export interface DropDirectoryReader {
  readEntries(success: (entries: readonly DropEntry[]) => void, error?: (err: unknown) => void): void;
}

export interface DropDirectoryEntry {
  readonly isFile: false;
  readonly isDirectory: true;
  readonly name: string;
  createReader(): DropDirectoryReader;
}

export type DropEntry = DropFileEntry | DropDirectoryEntry;

/** One dropped item: a folder or file entry, or a plain file (no entry API, or an item the API did not describe). */
export type DropSource =
  | { readonly kind: 'entry'; readonly entry: DropEntry; readonly file: File | null }
  | { readonly kind: 'file'; readonly file: File };

/** The subset of DataTransfer read here. */
export interface DropTransfer {
  readonly files?: ArrayLike<File> | null;
  readonly items?: ArrayLike<DropTransferItem> | null;
}

export interface DropTransferItem {
  readonly kind: string;
  getAsFile(): File | null;
  webkitGetAsEntry?: () => unknown;
}

export interface DroppedFiles {
  readonly files: File[];
  /** The drop held more than MAX_DROP_FILES files or folders deeper than MAX_DROP_DEPTH. */
  readonly truncated: boolean;
  /** At least one folder was dropped. */
  readonly folders: boolean;
}

const CLUTTER = new Set(['__macosx', 'thumbs.db', 'desktop.ini']);

/** Operating-system metadata nobody means to import. */
export function isClutter(name: string): boolean {
  return name.startsWith('.') || CLUTTER.has(name.toLowerCase());
}

function asEntry(x: unknown): DropEntry | null {
  if (typeof x !== 'object' || x === null) return null;
  const e = x as { readonly isFile?: unknown; readonly isDirectory?: unknown; readonly createReader?: unknown };
  const f = x as { readonly file?: unknown };
  if (e.isDirectory === true && typeof e.createReader === 'function') return x as DropDirectoryEntry;
  if (e.isFile === true && typeof f.file === 'function') return x as DropFileEntry;
  return null;
}

/**
 * The drop's items, taken while its event is dispatched (the browser empties the DataTransfer afterwards). Items
 * without the entry API fall back to the plain file list.
 */
export function takeDropEntries(dt: DropTransfer | null): DropSource[] {
  if (dt === null) return [];
  const items = dt.items === null || dt.items === undefined ? [] : Array.from(dt.items);
  const sources: DropSource[] = [];
  let described = false;
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = typeof item.webkitGetAsEntry === 'function' ? asEntry(item.webkitGetAsEntry()) : null;
    const file = item.getAsFile();
    if (entry !== null) {
      described = true;
      sources.push({ kind: 'entry', entry, file: entry.isFile ? file : null });
    } else if (file !== null) {
      sources.push({ kind: 'file', file });
    }
  }
  if (described || sources.length > 0) return sources;
  const files = dt.files === null || dt.files === undefined ? [] : Array.from(dt.files);
  return files.map((file) => ({ kind: 'file', file }));
}

/** The file's path below the dropped (or picked) folder, '' for a loose file. */
export function relativePathOf(file: File): string {
  const path = (file as Partial<Pick<File, 'webkitRelativePath'>>).webkitRelativePath;
  return typeof path === 'string' ? path : '';
}

/** Gives a file its path below the drop (own property: File.prototype only has a read-only getter). */
function withRelativePath(file: File, path: string): File {
  try {
    Object.defineProperty(file, 'webkitRelativePath', { value: path, enumerable: true, configurable: true });
    return file;
  } catch {
    // A frozen File (never in browsers): a copy that can carry the path.
    const copy = new File([file], file.name, { type: file.type, lastModified: file.lastModified });
    Object.defineProperty(copy, 'webkitRelativePath', { value: path, enumerable: true, configurable: true });
    return copy;
  }
}

function fileOf(entry: DropFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

/** Every entry of a folder (readEntries returns them in batches until an empty one). */
async function readAll(dir: DropDirectoryEntry): Promise<DropEntry[]> {
  const reader = dir.createReader();
  const out: DropEntry[] = [];
  for (;;) {
    const batch = await new Promise<readonly DropEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) return out;
    out.push(...batch);
    if (out.length > MAX_DROP_FILES) return out;
  }
}

/**
 * Expands the sources into files, folders recursively and in name order, each file from a folder with its relative
 * path. Unreadable entries are skipped (the rest still imports).
 */
export async function expandDropEntries(sources: readonly DropSource[]): Promise<DroppedFiles> {
  const files: File[] = [];
  let truncated = false;
  let folders = false;
  const full = (): boolean => {
    if (files.length < MAX_DROP_FILES) return false;
    truncated = true;
    return true;
  };

  // Files of one folder are read FILE_READS_AT_ONCE at a time (in order); folders one after the other.
  const readFiles = async (batch: ReadonlyArray<{ readonly entry: DropFileEntry; readonly path: string }>) => {
    for (let i = 0; i < batch.length; i += FILE_READS_AT_ONCE) {
      const got = await Promise.all(
        batch.slice(i, i + FILE_READS_AT_ONCE).map(async ({ entry, path }) => {
          try {
            return withRelativePath(await fileOf(entry), path);
          } catch {
            return null; // Unreadable (permissions, vanished): skipped.
          }
        }),
      );
      for (const f of got) {
        if (f === null) continue;
        if (full()) return;
        files.push(f);
      }
    }
  };

  const walk = async (dir: DropDirectoryEntry, path: string, depth: number): Promise<void> => {
    if (depth > MAX_DROP_DEPTH) {
      truncated = true;
      return;
    }
    let entries: DropEntry[];
    try {
      entries = await readAll(dir);
    } catch {
      return;
    }
    entries.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    let pending: Array<{ readonly entry: DropFileEntry; readonly path: string }> = [];
    for (const e of entries) {
      if (full()) return;
      if (isClutter(e.name)) continue;
      const child = `${path}/${e.name}`;
      if (e.isDirectory) {
        await readFiles(pending);
        pending = [];
        await walk(e, child, depth + 1);
      } else if (files.length + pending.length < MAX_DROP_FILES) {
        pending.push({ entry: e, path: child });
      } else {
        truncated = true;
      }
    }
    await readFiles(pending);
  };

  for (const src of sources) {
    if (full()) break;
    if (src.kind === 'file') {
      files.push(src.file);
      continue;
    }
    const { entry } = src;
    if (entry.isDirectory) {
      folders = true;
      if (isClutter(entry.name)) continue;
      await walk(entry, entry.name, 1);
      continue;
    }
    if (src.file !== null) {
      files.push(src.file);
      continue;
    }
    try {
      files.push(await fileOf(entry));
    } catch {
      // Unreadable: skipped.
    }
  }
  return { files, truncated, folders };
}
